import { useEffect, useMemo, useState } from "react";
import { Activity, Bot, ExternalLink, PiggyBank, Terminal, Users } from "lucide-react";
import type { YagniCodeFrontierComparison } from "@yagni/shared";
import { YAGNI_CODE_WORKER_AGENT_SLUGS } from "@yagni/shared";

import { enterGodMode } from "../lib/godMode.js";
import { combineClaudeSavings, type ClaudeSavingsPart } from "../usage/usageReport.js";

/* ═══════════════════════════════════════════════════════════
   AdminWorkspaceUsagePanel — super-admin, internal only.

   One workspace's spend over a range, in two halves:
   - YAGNI Code per-developer spend (Track 1, sell side) — the same
     rollup the workspace-admin Settings surface reads.
   - Agent spend (internal cost side), pivotable by Team, by Agent,
     or Team × Agent: our model and sandbox COGS beside what the
     workspace was charged for the same work.

   ADR-0028: Teams are flat-priced with no customer-facing meter.
   Our cost per Worker exists ONLY here, behind the super-admin gate:
   never lift this panel (or its endpoint) onto a customer surface.
   The sell side and its Claude comparison are what the customer's
   own Usage report shows; "Open customer report" and an Agent row
   open that report as the workspace (god mode, View as workspace).
   ═══════════════════════════════════════════════════════════ */

/* ─── Types (matching backend response shapes) ─────────────── */

interface UsageLaneCell {
  lane: string;
  sellMillicents: number;
  dispatches: number;
}

interface UsageDeveloperRow {
  userId: string | null;
  displayName: string | null;
  email: string | null;
  formerMember: boolean;
  lanes: UsageLaneCell[];
  totalSellMillicents: number;
  totalDispatches: number;
  /** The Claude list comparison (matched tier). Optional: older backends
   * omit it. */
  totalCounterfactualMillicents?: number;
  totalSavingsMillicents?: number;
  counterfactualIncomplete?: boolean;
}

interface ClientUsageCell {
  client: "yagni-code" | "claude-code" | "codex";
  dispatches: number;
  sellMillicents: number;
  promptTokens: number;
  completionTokens: number;
}

interface DeveloperClientCell extends ClientUsageCell {
  userId: string | null;
}

interface CodeUsageReport {
  from: string;
  to: string;
  developers: UsageDeveloperRow[];
  totalSellMillicents: number;
  totalDispatches: number;
  unattributedSellMillicents: number;
  unbilledSellMillicents: number;
  /** The Claude list comparison (matched tier). Optional: older backends
   * omit it. */
  totalCounterfactualMillicents?: number;
  totalSavingsMillicents?: number;
  counterfactualIncomplete?: boolean;
  /** Optional: older backends predate the client dimension. */
  clients?: ClientUsageCell[];
  developerClients?: DeveloperClientCell[];
}

interface WorkerUsageCell {
  deskId: string | null;
  deskName: string | null;
  personaSlug: string | null;
  runCount: number;
  /** Our cost: model COGS plus sandbox COGS. */
  costMillicents: number;
  /** What the workspace was charged for the same dispatches and sandboxes. */
  sellMillicents: number;
  /** The Claude comparison for the rate-card-priced part of `sellMillicents`.
   * Absent when the backend's pricing read failed. */
  frontier?: YagniCodeFrontierComparison;
}

interface WorkerUsageReport {
  from: string;
  to: string;
  cells: WorkerUsageCell[];
  totalCostMillicents: number;
  totalSellMillicents: number;
  totalRunCount: number;
  frontier?: YagniCodeFrontierComparison;
}

export interface WorkspaceUsageDetail {
  workspaceId: string;
  code: CodeUsageReport;
  workers: WorkerUsageReport;
}

export interface UsageDetailRange {
  /** Inclusive UTC day, YYYY-MM-DD. */
  from: string;
  /** Exclusive UTC day, YYYY-MM-DD. */
  to: string;
}

export type FetchWorkspaceUsageDetail = (
  workspaceId: string,
  range: UsageDetailRange,
) => Promise<WorkspaceUsageDetail>;

const defaultFetchDetail: FetchWorkspaceUsageDetail = async (workspaceId, { from, to }) => {
  const res = await fetch(
    `/api/admin/usage-detail/${encodeURIComponent(workspaceId)}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    { credentials: "include" },
  );
  if (!res.ok) throw new Error(`Failed to load usage detail (${res.status})`);
  return (await res.json()) as WorkspaceUsageDetail;
};

/* ─── Helpers ──────────────────────────────────────────────── */

function formatUsd(millicents: number): string {
  return `$${(millicents / 100_000).toFixed(2)}`;
}

/**
 * Saved vs Claude for the workspace: the same combination the customer's
 * headline uses, code and Agents together, floored once when every dollar was
 * priced. Null when the code report carries no Claude comparison; the Agents
 * join only when their pricing read succeeded.
 */
function savedVsClaude(
  code: CodeUsageReport,
  workers: WorkerUsageReport,
): ReturnType<typeof combineClaudeSavings> | null {
  if (code.totalCounterfactualMillicents === undefined) return null;
  const parts: ClaudeSavingsPart[] = [
    {
      billedMillicents: code.totalSellMillicents,
      claudeMillicents: code.totalCounterfactualMillicents,
      savedMillicents: code.totalSavingsMillicents ?? 0,
      incomplete: code.counterfactualIncomplete ?? false,
    },
  ];
  if (workers.frontier) {
    parts.push({
      billedMillicents: workers.totalSellMillicents,
      claudeMillicents: workers.frontier.matched.totalMillicents,
      savedMillicents: workers.frontier.matched.savingsMillicents,
      incomplete: workers.frontier.counterfactualIncomplete,
    });
  }
  return combineClaudeSavings(parts);
}

const DAY_MS = 24 * 60 * 60 * 1000;

function toIsoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function todayUtc(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
}

type RangePreset = "this-month" | "last-30" | "last-90";

const PRESETS: Array<{ key: RangePreset; label: string }> = [
  { key: "this-month", label: "This month" },
  { key: "last-30", label: "Last 30 days" },
  { key: "last-90", label: "Last 90 days" },
];

function presetRange(preset: RangePreset): UsageDetailRange {
  const today = todayUtc();
  const tomorrow = new Date(today.getTime() + DAY_MS);
  if (preset === "this-month") {
    return {
      from: toIsoDay(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1))),
      to: toIsoDay(tomorrow),
    };
  }
  const days = preset === "last-30" ? 30 : 90;
  return {
    from: toIsoDay(new Date(today.getTime() - (days - 1) * DAY_MS)),
    to: toIsoDay(tomorrow),
  };
}

const LANE_ORDER = ["efficient", "standard", "advanced", "peak"];

const LANE_LABELS: Record<string, string> = {
  efficient: "Efficient",
  standard: "Standard",
  advanced: "Advanced",
  peak: "Peak",
};

const CLIENT_LABELS: Record<ClientUsageCell["client"], string> = {
  "yagni-code": "YAGNI Code",
  "claude-code": "Claude Code",
  codex: "Codex",
};

function teamLabel(cell: { deskId: string | null; deskName: string | null }): string {
  if (cell.deskId === null) return "No Team";
  return cell.deskName ?? "(deleted team)";
}

/** An Agent's registry slug → the key of its customer drill: its pipeline
 * `llm_usage.agent_id`, or for Wright, whose spend is all run dispatches,
 * `wright`. A run with no Agent opens the "Team runs" drill. */
const AGENT_ID_BY_SLUG: ReadonlyMap<string, string> = new Map([
  ...Object.entries(YAGNI_CODE_WORKER_AGENT_SLUGS).map(([agentId, slug]) => [slug, agentId] as const),
  ["wright", "wright"],
]);

export function agentIdForSlug(personaSlug: string | null): string | null {
  return personaSlug === null ? "team_run" : (AGENT_ID_BY_SLUG.get(personaSlug) ?? null);
}

function workerLabel(personaSlug: string | null): string {
  if (personaSlug === null) return "Team runs (no Agent)";
  const words = personaSlug.replace(/[-_]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

type WorkerPivot = "team" | "worker" | "matrix";

/** A row's Claude comparison on the matched tier. Summed across cells, so
 * each cell's savings floor stays its own (the panel's footer uses the
 * report's floor-once total instead). */
interface PivotClaude {
  claudeMillicents: number;
  savedMillicents: number;
}

interface PivotRow {
  key: string;
  label: string;
  sublabel?: string;
  /** Set when the row is one Worker with a customer drill. */
  agentId?: string | null;
  runCount: number;
  costMillicents: number;
  sellMillicents: number;
  /** Null when any cell behind the row lacks a comparison. */
  claude: PivotClaude | null;
}

function cellClaude(cell: WorkerUsageCell): PivotClaude | null {
  if (!cell.frontier) return null;
  return {
    claudeMillicents: cell.frontier.matched.totalMillicents,
    savedMillicents: cell.frontier.matched.savingsMillicents,
  };
}

function aggregate(
  cells: readonly WorkerUsageCell[],
  keyOf: (cell: WorkerUsageCell) => string,
  labelOf: (cell: WorkerUsageCell) => string,
  agentIdOf?: (cell: WorkerUsageCell) => string | null,
): PivotRow[] {
  const byKey = new Map<string, PivotRow>();
  for (const cell of cells) {
    const key = keyOf(cell);
    const existing = byKey.get(key);
    const row = existing ?? {
      key,
      label: labelOf(cell),
      agentId: agentIdOf?.(cell),
      runCount: 0,
      costMillicents: 0,
      sellMillicents: 0,
      claude: { claudeMillicents: 0, savedMillicents: 0 },
    };
    row.runCount += cell.runCount;
    row.costMillicents += cell.costMillicents;
    row.sellMillicents += cell.sellMillicents;
    const claude = cellClaude(cell);
    row.claude =
      row.claude && claude
        ? {
            claudeMillicents: row.claude.claudeMillicents + claude.claudeMillicents,
            savedMillicents: row.claude.savedMillicents + claude.savedMillicents,
          }
        : null;
    byKey.set(key, row);
  }
  return [...byKey.values()].sort(
    (a, b) => b.costMillicents - a.costMillicents || b.runCount - a.runCount,
  );
}

/* ─── Local table chrome (AdminView conventions) ───────────── */

function Table({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="overflow-x-auto rounded-[var(--radius-card)] border"
      style={{ borderColor: "var(--print-border)", background: "var(--print-bg)", boxShadow: "var(--shadow-print)" }}
    >
      <table className="w-full text-[13px]" style={{ color: "var(--text-primary)" }}>
        {children}
      </table>
    </div>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return (
    <th
      className="text-left px-3 py-2 text-[11px] uppercase tracking-wider font-medium border-b"
      style={{ color: "var(--text-label)", borderColor: "var(--border-subtle)", background: "rgba(var(--ink-rgb), 0.04)", fontFamily: "var(--font-mono)" }}
    >
      {children}
    </th>
  );
}

function Td({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  return (
    <td
      className={`px-3 py-2 border-b ${mono ? "font-mono text-[12px]" : ""}`}
      style={{ borderColor: "var(--border-subtle)" }}
    >
      {children}
    </td>
  );
}

function StatCard({ label, value, sub, icon }: { label: string; value: string; sub?: string; icon: React.ReactNode }) {
  return (
    <div
      className="rounded-[var(--radius-card)] border p-4 flex items-start gap-3"
      style={{ borderColor: "var(--print-border)", background: "var(--print-bg)", boxShadow: "var(--shadow-print)" }}
    >
      <div className="p-2 rounded-lg" style={{ background: "var(--accent-muted)" }}>{icon}</div>
      <div>
        <div className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "var(--text-label)", fontFamily: "var(--font-mono)" }}>
          {label}
        </div>
        <div className="text-xl font-semibold mt-0.5" style={{ color: "var(--text-primary)" }}>{value}</div>
        {sub && <div className="text-[12px] mt-0.5" style={{ color: "var(--text-secondary)" }}>{sub}</div>}
      </div>
    </div>
  );
}

function SectionHeader({ title, icon }: { title: string; icon: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      {icon}
      <h2 className="text-[14px] font-semibold m-0" style={{ color: "var(--text-primary)" }}>{title}</h2>
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="px-2.5 py-1 rounded text-[12px] border-none cursor-pointer"
      style={
        active
          ? { background: "var(--accent)", color: "var(--text-on-accent)" }
          : { background: "rgba(var(--ink-rgb), 0.06)", color: "var(--text-secondary)" }
      }
    >
      {children}
    </button>
  );
}

function optionalUsd(millicents: number | undefined): string {
  return millicents === undefined ? "-" : formatUsd(millicents);
}

/** A Worker's name; a link to its customer drill when it has one. */
function AgentName({
  label,
  agentId,
  onOpen,
}: {
  label: string;
  agentId: string | null;
  onOpen: (agentId: string) => void;
}) {
  if (agentId === null) return <>{label}</>;
  return (
    <button
      type="button"
      onClick={() => onOpen(agentId)}
      className="p-0 border-0 bg-transparent cursor-pointer underline-offset-2 hover:underline"
      style={{ color: "var(--accent)", font: "inherit" }}
      title="View as this workspace and open the Agent's report"
    >
      {label}
    </button>
  );
}

/* ─── The panel ────────────────────────────────────────────── */

export type OpenInWorkspace = (workspaceId: string, path: string) => void;

/** View as the workspace (god mode), landing on `path` in its product. */
const defaultOpenInWorkspace: OpenInWorkspace = (workspaceId, path) => {
  void enterGodMode(workspaceId, path).then((result) => {
    if (!result.ok) alert(`Failed to enter god mode: ${result.error}`);
  });
};

export interface AdminWorkspaceUsagePanelProps {
  workspaceId: string;
  /** Injected in tests; defaults to GET /api/admin/usage-detail/:workspaceId. */
  fetchDetail?: FetchWorkspaceUsageDetail;
  /** Injected in tests; defaults to entering god mode on the workspace. */
  openInWorkspace?: OpenInWorkspace;
}

export function AdminWorkspaceUsagePanel({
  workspaceId,
  fetchDetail = defaultFetchDetail,
  openInWorkspace = defaultOpenInWorkspace,
}: AdminWorkspaceUsagePanelProps) {
  const [preset, setPreset] = useState<RangePreset>("last-30");
  const [pivot, setPivot] = useState<WorkerPivot>("team");
  const [detail, setDetail] = useState<WorkspaceUsageDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchDetail(workspaceId, presetRange(preset))
      .then((loaded) => {
        if (cancelled) return;
        setDetail(loaded);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load usage detail");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchDetail, workspaceId, preset]);

  const laneColumns = useMemo(() => {
    const known = new Set(LANE_ORDER);
    const extras =
      detail?.code.developers
        .flatMap((row) => row.lanes.map((lane) => lane.lane))
        .filter((lane) => !known.has(lane)) ?? [];
    return [...LANE_ORDER, ...[...new Set(extras)].sort()];
  }, [detail]);

  const workerRows = useMemo<PivotRow[]>(() => {
    const cells = detail?.workers.cells ?? [];
    if (pivot === "team") {
      return aggregate(cells, (cell) => cell.deskId ?? "∅", teamLabel);
    }
    if (pivot === "worker") {
      return aggregate(
        cells,
        (cell) => cell.personaSlug ?? "∅",
        (cell) => workerLabel(cell.personaSlug),
        (cell) => agentIdForSlug(cell.personaSlug),
      );
    }
    // Matrix: one row per Team × Worker cell, grouped by team spend.
    const teamTotals = new Map<string, number>();
    for (const cell of cells) {
      const key = cell.deskId ?? "∅";
      teamTotals.set(key, (teamTotals.get(key) ?? 0) + cell.costMillicents);
    }
    return [...cells]
      .sort((a, b) => {
        const teamDelta =
          (teamTotals.get(b.deskId ?? "∅") ?? 0) - (teamTotals.get(a.deskId ?? "∅") ?? 0);
        return teamDelta || b.costMillicents - a.costMillicents;
      })
      .map((cell) => ({
        key: `${cell.deskId ?? "∅"}:${cell.personaSlug ?? "∅"}`,
        label: teamLabel(cell),
        sublabel: workerLabel(cell.personaSlug),
        agentId: agentIdForSlug(cell.personaSlug),
        runCount: cell.runCount,
        costMillicents: cell.costMillicents,
        sellMillicents: cell.sellMillicents,
        claude: cellClaude(cell),
      }));
  }, [detail, pivot]);

  if (loading) {
    return (
      <div className="text-[13px]" style={{ color: "var(--text-tertiary)" }}>
        Loading usage detail…
      </div>
    );
  }
  if (error || !detail) {
    return (
      <div className="text-[13px]" role="alert" style={{ color: "var(--error)" }}>
        {error ?? "Failed to load usage detail"}
      </div>
    );
  }

  const { code, workers } = detail;
  const workerTotal = workers.totalCostMillicents;
  const range = presetRange(preset);
  const drillQuery = `from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`;
  const openAgent = (agentId: string) =>
    openInWorkspace(workspaceId, `/usage/agents/${encodeURIComponent(agentId)}?${drillQuery}`);
  const saved = savedVsClaude(code, workers);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map(({ key, label }) => (
          <ToggleButton key={key} active={preset === key} onClick={() => setPreset(key)}>
            {label}
          </ToggleButton>
        ))}
        <span className="text-[12px]" style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
          {code.from} through {code.to} (exclusive, UTC)
        </span>
        <button
          type="button"
          onClick={() => openInWorkspace(workspaceId, "/usage")}
          className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[12px] border cursor-pointer"
          style={{ borderColor: "var(--border)", background: "var(--print-bg-solid)", color: "var(--text-primary)" }}
          title="View as this workspace and open its Usage report"
        >
          <ExternalLink size={12} /> Open customer report
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          label="Code spend (sell)"
          value={formatUsd(code.totalSellMillicents)}
          sub={`${code.totalDispatches.toLocaleString("en-US")} dispatches`}
          icon={<Terminal size={16} style={{ color: "var(--accent)" }} />}
        />
        <StatCard
          label="Agent run spend (cost)"
          value={formatUsd(workerTotal)}
          sub={`${workers.totalRunCount.toLocaleString("en-US")} runs`}
          icon={<Bot size={16} style={{ color: "var(--accent)" }} />}
        />
        <StatCard
          label="Developers with usage"
          value={String(code.developers.filter((row) => row.userId !== null).length)}
          icon={<Users size={16} style={{ color: "var(--success)" }} />}
        />
        <StatCard
          label="Unattributed code spend"
          value={formatUsd(code.unattributedSellMillicents)}
          sub={code.unbilledSellMillicents > 0 ? `${formatUsd(code.unbilledSellMillicents)} unbilled` : undefined}
          icon={<Activity size={16} style={{ color: "var(--warning)" }} />}
        />
        <StatCard
          label="Saved vs Claude"
          value={saved ? formatUsd(saved.savedMillicents) : "-"}
          sub={
            saved
              ? `${saved.percent !== null ? `${saved.percent}% of ` : ""}${formatUsd(saved.claudeMillicents)} at Claude list${workers.frontier ? "" : ", code only"}`
              : "comparison unavailable"
          }
          icon={<PiggyBank size={16} style={{ color: "var(--accent-active)" }} />}
        />
      </div>

      <div>
        <SectionHeader
          title="YAGNI Code — spend by developer (sell side)"
          icon={<Terminal size={16} style={{ color: "var(--accent)" }} />}
        />
        <Table>
          <thead>
            <tr>
              <Th>Developer</Th>
              {laneColumns.map((lane) => (
                <Th key={lane}>{LANE_LABELS[lane] ?? lane}</Th>
              ))}
              <Th>Dispatches</Th>
              <Th>Total</Th>
              <Th>At Claude list</Th>
              <Th>Saved</Th>
            </tr>
          </thead>
          <tbody>
            {code.developers.map((developer) => {
              const key = developer.userId ?? "unattributed";
              return (
                <tr key={key}>
                  <Td>
                    {developer.userId === null ? (
                      <span className="italic" style={{ color: "var(--text-secondary)" }}>
                        Not attributed to a seat
                      </span>
                    ) : (
                      <div>
                        <div className="font-medium">
                          {developer.displayName ?? "Former member"}
                          {developer.formerMember ? " (departed)" : ""}
                        </div>
                        {developer.email && (
                          <div className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                            {developer.email}
                          </div>
                        )}
                      </div>
                    )}
                  </Td>
                  {laneColumns.map((lane) => {
                    const cell = developer.lanes.find((candidate) => candidate.lane === lane);
                    return (
                      <Td key={lane} mono>
                        {cell ? formatUsd(cell.sellMillicents) : "—"}
                      </Td>
                    );
                  })}
                  <Td mono>{developer.totalDispatches.toLocaleString("en-US")}</Td>
                  <Td mono>{formatUsd(developer.totalSellMillicents)}</Td>
                  <Td mono>{optionalUsd(developer.totalCounterfactualMillicents)}</Td>
                  <Td mono>{optionalUsd(developer.totalSavingsMillicents)}</Td>
                </tr>
              );
            })}
            {code.developers.length === 0 && (
              <tr>
                <Td>No YAGNI Code usage in this range</Td>
              </tr>
            )}
          </tbody>
          {code.developers.length > 0 && (
            <tfoot>
              <tr>
                <Td>
                  <span className="font-semibold">Workspace total</span>
                </Td>
                {laneColumns.map((lane) => (
                  <Td key={lane}>{""}</Td>
                ))}
                <Td mono>{code.totalDispatches.toLocaleString("en-US")}</Td>
                <Td mono>
                  <span className="font-semibold">{formatUsd(code.totalSellMillicents)}</span>
                </Td>
                <Td mono>{optionalUsd(code.totalCounterfactualMillicents)}</Td>
                <Td mono>
                  <span className="font-semibold">{optionalUsd(code.totalSavingsMillicents)}</span>
                </Td>
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      {code.clients && code.clients.length > 0 && (
        <div data-testid="client-mix">
          <SectionHeader
            title="Coding clients"
            icon={<Terminal size={16} style={{ color: "var(--accent)" }} />}
          />
          <Table>
            <thead>
              <tr>
                <Th>Client</Th>
                <Th>Dispatches</Th>
                <Th>Tokens (in / out)</Th>
                <Th>Spend</Th>
                <Th>Share</Th>
                <Th>Developers</Th>
              </tr>
            </thead>
            <tbody>
              {code.clients.map((cell) => {
                const names = (code.developerClients ?? [])
                  .filter((row) => row.client === cell.client)
                  .map((row) => {
                    if (row.userId === null) return "unattributed";
                    const developer = code.developers.find((d) => d.userId === row.userId);
                    return developer?.displayName ?? developer?.email ?? "former member";
                  });
                return (
                  <tr key={cell.client}>
                    <Td>
                      <span className="font-medium">{CLIENT_LABELS[cell.client] ?? cell.client}</span>
                    </Td>
                    <Td mono>{cell.dispatches.toLocaleString("en-US")}</Td>
                    <Td mono>
                      {cell.promptTokens.toLocaleString("en-US")} / {cell.completionTokens.toLocaleString("en-US")}
                    </Td>
                    <Td mono>{formatUsd(cell.sellMillicents)}</Td>
                    <Td mono>
                      {code.totalSellMillicents > 0
                        ? `${((cell.sellMillicents / code.totalSellMillicents) * 100).toFixed(1)}%`
                        : "—"}
                    </Td>
                    <Td>{names.join(", ") || "—"}</Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <p className="text-[12px] mt-2" style={{ color: "var(--text-tertiary)" }}>
            Derived from each dispatch's caller attribution: Claude Code and Codex traffic carries
            the label its `yagni connect` config stamps; everything else is the YAGNI Code
            CLI/desktop harness. Labels are display-only. Billing never keys on them.
          </p>
        </div>
      )}

      <div>
        <SectionHeader
          title="Agents · spend (internal cost, never customer-facing)"
          icon={<Bot size={16} style={{ color: "var(--accent)" }} />}
        />
        <div className="flex items-center gap-2 mb-3">
          <ToggleButton active={pivot === "team"} onClick={() => setPivot("team")}>
            By team
          </ToggleButton>
          <ToggleButton active={pivot === "worker"} onClick={() => setPivot("worker")}>
            By Agent
          </ToggleButton>
          <ToggleButton active={pivot === "matrix"} onClick={() => setPivot("matrix")}>
            Team × Agent
          </ToggleButton>
        </div>
        <Table>
          <thead>
            <tr>
              <Th>{pivot === "worker" ? "Agent" : "Team"}</Th>
              {pivot === "matrix" && <Th>Agent</Th>}
              <Th>Runs</Th>
              <Th>Our cost</Th>
              <Th>Charged</Th>
              <Th>At Claude list</Th>
              <Th>Saved</Th>
              <Th>Share</Th>
            </tr>
          </thead>
          <tbody>
            {workerRows.map((row) => (
              <tr key={row.key}>
                <Td>
                  {pivot === "worker" ? (
                    <AgentName label={row.label} agentId={row.agentId ?? null} onOpen={openAgent} />
                  ) : (
                    row.label
                  )}
                </Td>
                {pivot === "matrix" && (
                  <Td>
                    {row.sublabel ? (
                      <AgentName label={row.sublabel} agentId={row.agentId ?? null} onOpen={openAgent} />
                    ) : (
                      "—"
                    )}
                  </Td>
                )}
                <Td mono>{row.runCount.toLocaleString("en-US")}</Td>
                <Td mono>{formatUsd(row.costMillicents)}</Td>
                <Td mono>{row.sellMillicents > 0 ? formatUsd(row.sellMillicents) : "—"}</Td>
                <Td mono>{row.claude && row.sellMillicents > 0 ? formatUsd(row.claude.claudeMillicents) : "-"}</Td>
                <Td mono>{row.claude && row.sellMillicents > 0 ? formatUsd(row.claude.savedMillicents) : "-"}</Td>
                <Td mono>
                  {workerTotal > 0
                    ? `${((row.costMillicents / workerTotal) * 100).toFixed(1)}%`
                    : "—"}
                </Td>
              </tr>
            ))}
            {workerRows.length === 0 && (
              <tr>
                <Td>No Agent spend in this range</Td>
              </tr>
            )}
          </tbody>
          {workerRows.length > 0 && (
            <tfoot>
              <tr>
                <Td>
                  <span className="font-semibold">Total</span>
                </Td>
                {pivot === "matrix" && <Td>{""}</Td>}
                <Td mono>{workers.totalRunCount.toLocaleString("en-US")}</Td>
                <Td mono>
                  <span className="font-semibold">{formatUsd(workerTotal)}</span>
                </Td>
                <Td mono>
                  <span className="font-semibold">
                    {workers.totalSellMillicents > 0
                      ? formatUsd(workers.totalSellMillicents)
                      : "—"}
                  </span>
                </Td>
                <Td mono>
                  {workers.frontier && workers.totalSellMillicents > 0
                    ? formatUsd(workers.frontier.matched.totalMillicents)
                    : "-"}
                </Td>
                <Td mono>
                  <span className="font-semibold">
                    {workers.frontier && workers.totalSellMillicents > 0
                      ? formatUsd(workers.frontier.matched.savingsMillicents)
                      : "-"}
                  </span>
                </Td>
                <Td>{""}</Td>
              </tr>
            </tfoot>
          )}
        </Table>
        <p className="text-[12px] mt-2" style={{ color: "var(--text-tertiary)" }}>
          Our cost is what the work cost us: the model COGS of every dispatch an Agent made
          (inside its own runs, and for Bailey and Harper with no run) plus the sandbox COGS of
          those runs. Charged is what the workspace paid for the same work: the rate-card price
          of each dispatch (or COGS at the old 2x markup where no card priced it) plus sandbox
          at 2x. Each dispatch counts on the day it was recorded, on the Team its run served or
          the Team a runless Agent worked for. At Claude list and Saved compare the card-priced
          dispatches with the same tokens at Claude list on the matched tier, prompt writing
          included, exactly as the customer's Usage report does. Our cost stays behind the
          super-admin gate and is never shown to customers. Open an Agent to see its customer
          drill as the workspace.
        </p>
      </div>
    </div>
  );
}

export default AdminWorkspaceUsagePanel;
